/**
 * One timestamp shared by everything in this app that can programmatically
 * reload the tab, plus the storage-health probe that decides whether the
 * automatic paths are allowed to act at all.
 *
 * The full roster of programmatic reload() sites (audited 2026-08-02,
 * updated 2026-08-13 when the stale-chunk path moved to lazyRoute.ts):
 *
 *   1. lazyRoute.ts - every lazy-loaded route's dynamic import() factory is
 *      wrapped there, and its failure path is the ONE caller of
 *      triggerStaleChunkReload() below. main.tsx used to listen for Vite's
 *      vite:preloadError itself and call this same sequence inline; that
 *      listener is gone (see main.tsx's comment and lazyRoute.ts's doc
 *      comment for why keeping both was itself the bug - a global event
 *      listener fires synchronously INSIDE the promise chain lazyRoute.ts
 *      awaits, so it always reached the guard first and left lazyRoute.ts's
 *      own attempt permanently cooldown-blocked, proven with a probe before
 *      this rewrite). Since 2026-10-24 it first asks whether a reload would
 *      throw something away, and if so holds it for the update pill instead
 *      (THE STALE-CHUNK HOLD, at the bottom of this file);
 *   2. freshness.ts - the version auto-update;
 *   3. auth.ts signOut() - reloads after terminating Firestore and wiping
 *      IndexedDB persistence (the shared-iPad minors-data wipe);
 *   4. Landing.tsx acceptMyInvite - reloads to pick up fresh custom claims.
 *   5. firestoreRecovery.ts (packet B815, 2026-10-19) - one reload when an
 *      iPhone has closed the Hub's database under the page. It is AUTOMATIC,
 *      so it refuses inside the cooldown like 1 and 2, stamps it like all
 *      of them, and also keeps its own ten minute window in sessionStorage
 *      (lib/firestoreClosedRecovery.ts). Its bar's tap is a human recovery
 *      tap, like the buttons below.
 *
 * The first two fire on the same underlying event (a deploy landed while the
 * tab was open), so left independent they can double-reload or take turns:
 * the freshness reload lands, a lazy import on the dying page fails, and
 * lazyRoute.ts reloads the already-fresh page. The last two are deliberate
 * user actions, and the danger runs the other way: a freshness check racing
 * the sign-out reload could interrupt the persistence wipe mid-flight. All
 * four stamp the one timestamp; only the two AUTOMATIC paths also refuse to
 * act inside the cooldown window.
 *
 * The various user-facing "Reload" recovery buttons (error boundary, loading
 * watchdog, video error states) are deliberately NOT on this list: they are
 * explicit human recovery taps and must never be blocked. The pill's own
 * button stamps the cooldown on the user's behalf.
 *
 * The cooldown lives in sessionStorage on purpose: it survives the reload we
 * are guarding against (same tab, same session) but not an app relaunch, so
 * a genuinely stuck install gets a fresh attempt per launch. The RETRY
 * MARKER (which deployed version we already burned an automatic reload on)
 * lives in localStorage for exactly the opposite reason: on an installed
 * PWA every relaunch is a NEW session, so a sessionStorage marker resets on
 * each launch and the exact device this feature targets would eat a reload
 * flash on every open while never once seeing the pill.
 */

// Explicit .ts extensions (allowImportingTsExtensions in tsconfig.json):
// triggerStaleChunkReload() below is imported directly by node:test under
// --experimental-strip-types (the lazy-wrapper bite tests), and that loader
// will not resolve an extensionless relative specifier. Same rule as
// lib/alternates.ts.
import { RELOAD_COOLDOWN_MS } from './unsavedWork.ts';
import { captureSentryMessage } from './sentry.ts';

const RELOAD_TS_KEY = 'ldg-reload-at';
const RETRY_TARGET_KEY = 'ldg-freshness-reload-target';
const PROBE_KEY = 'ldg-guard-probe';

/**
 * Both storage areas survive a real write/read/remove round trip. Every
 * anti-loop guard lives in one of the two, so a throwing storage means they
 * are ALL gone at once - the review drove the previous code under a throwing
 * sessionStorage and produced an unbounded reload loop. Callers must treat
 * false as "never auto-reload": the pill works without storage, the reload
 * does not.
 */
export function reloadGuardsHealthy(): boolean {
  try {
    window.sessionStorage.setItem(PROBE_KEY, '1');
    if (window.sessionStorage.getItem(PROBE_KEY) !== '1') return false;
    window.sessionStorage.removeItem(PROBE_KEY);
    window.localStorage.setItem(PROBE_KEY, '1');
    if (window.localStorage.getItem(PROBE_KEY) !== '1') return false;
    window.localStorage.removeItem(PROBE_KEY);
    return true;
  } catch {
    return false;
  }
}

/**
 * Milliseconds since anything last programmatically reloaded this tab.
 * Infinity when nothing has, so a first-ever reload is never blocked.
 * A THROWING storage also reports Infinity - that is safe only because
 * every automatic caller checks reloadGuardsHealthy() first and refuses
 * to reload at all when storage is broken (fail closed, per the review).
 */
export function msSinceLastReload(): number {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_TS_KEY);
    if (raw === null) return Infinity;
    const last = Number(raw);
    if (!Number.isFinite(last)) return Infinity;
    return Date.now() - last;
  } catch {
    return Infinity;
  }
}

/** Stamp the shared cooldown. Call immediately before any programmatic reload. */
export function markReload(): void {
  try {
    window.sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()));
  } catch {
    // Best effort. The automatic paths never reach here with broken storage
    // (reloadGuardsHealthy gates them); the deliberate paths (sign-out,
    // invite) must proceed regardless.
  }
}

/**
 * The deployed version we already spent our one automatic reload attempt on.
 * null when none is recorded or storage is unreadable.
 */
export function readRetryTarget(): string | null {
  try {
    return window.localStorage.getItem(RETRY_TARGET_KEY);
  } catch {
    return null;
  }
}

/**
 * Record that we are about to auto-reload toward `version`. Returns false
 * when the write did not stick, and the caller MUST NOT reload in that case:
 * a reload whose marker was never written is a reload that can repeat
 * forever.
 */
export function writeRetryTarget(version: string): boolean {
  try {
    window.localStorage.setItem(RETRY_TARGET_KEY, version);
    return window.localStorage.getItem(RETRY_TARGET_KEY) === version;
  } catch {
    return false;
  }
}

/** The reload landed on the build it aimed at; let a future deploy try again. */
export function clearRetryTarget(): void {
  try {
    window.localStorage.removeItem(RETRY_TARGET_KEY);
  } catch {
    // Ignored: a stale marker for an OLD version never blocks a NEW version,
    // because the guard compares the marker against the deployed version.
  }
}

/**
 * The shared ACTION behind every "this looks like a stale chunk" failure -
 * checks the guard, and when it is healthy and outside the cooldown, stamps
 * the timestamp, reports the same 'stale-chunk reload' Sentry breadcrumb this
 * app has always used, and reloads. Returns false without doing any of that
 * when the guard says no (storage broken, or a reload already happened in
 * the last RELOAD_COOLDOWN_MS) - the caller's job at that point is to let the
 * real failure surface instead of trying again, since a second failure this
 * close together is a genuinely broken chunk or an offline network, not
 * routine staleness.
 *
 * ONE caller, on purpose (design law: one thing in one place): lazyRoute.ts's
 * dynamic import() factory wrapper. This sequence used to live inline in
 * main.tsx's own vite:preloadError listener, and moved here UNCHANGED so
 * lazyRoute.ts could reuse it rather than growing a second, slightly
 * different version of "reload the tab." main.tsx's listener was then
 * deleted rather than left calling this too - see its comment for why a
 * second caller is actively wrong here, not just redundant: it would win the
 * guard first every single time and leave lazyRoute.ts's own attempt always
 * blocked.
 */
export function triggerStaleChunkReload(): boolean {
  if (!reloadGuardsHealthy()) return false;
  if (msSinceLastReload() < RELOAD_COOLDOWN_MS) return false;
  markReload();
  // Telemetry before the page dies, so we can see how often deploys strand
  // open tabs. No-op when Sentry has no DSN. If the SDK has not loaded yet
  // it is queued, and since the reload below tears that queue down, the
  // page's pagehide writes it to sessionStorage for the next page load to
  // send (lib/sentry.ts, CARRY_KEY).
  captureSentryMessage('stale-chunk reload', 'info');
  window.location.reload();
  return true;
}

/*
 * THE STALE-CHUNK HOLD (train follow-up, 2026-10-24).
 *
 * The version auto-update and the reload after an iPhone closes the Hub's
 * database both wait while a reload would throw something away, and the How
 * To guide promises it: "the Hub does not reload itself for an app update"
 * while a role, a term run or anything else that warns before you leave is
 * unsaved. The stale-chunk reload above did not ask. Opening a part of the
 * Hub that loads on demand (the Assistant panel, the rich message editor)
 * after an update had replaced its file reloaded the page there and then,
 * and on an iPhone or iPad nothing asks first, so a Director's half-built
 * role went with it.
 *
 * lazyRoute.ts now asks first. When a reload would lose something, it holds
 * the reload here instead of doing it, and the part that could not load
 * waits on its own loading screen. The way forward is the update pill
 * (GlobalChrome's FreshnessPill, "An update is ready. Finish up and tap
 * Reload."), which shows for a held reload and stays: the part that failed
 * cannot load again in this page's life, so only a reload brings it back.
 *
 * A hold needs something on screen to offer the reload, or the person is
 * left with a loading screen and nothing to tap. FreshnessPill registers
 * itself with offerHeldReload() while it is mounted; with nothing
 * registered (a dancer-only session has no pill), holdStaleChunkReload()
 * refuses and the caller reloads as it always did.
 */
let staleChunkHeld = false;
let heldReloadOffers = 0;
const staleChunkHoldListeners = new Set<() => void>();

/**
 * Something on screen can offer a held reload (FreshnessPill, while it is
 * mounted). Returns the release, which is safe to call more than once.
 */
export function offerHeldReload(): () => void {
  heldReloadOffers += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    heldReloadOffers -= 1;
  };
}

/** True once a stale-chunk reload was held on this page. It stays true. */
export function staleChunkReloadHeld(): boolean {
  return staleChunkHeld;
}

/** Hear the hold. Returns the unsubscribe. */
export function subscribeStaleChunkHold(listener: () => void): () => void {
  staleChunkHoldListeners.add(listener);
  return () => {
    staleChunkHoldListeners.delete(listener);
  };
}

/**
 * Hold a stale-chunk reload instead of doing it. False, with nothing
 * changed, when nothing on screen can offer the reload: the caller then
 * reloads as before rather than leave the person stuck. True when held: the
 * listeners (the pill) have been told.
 */
export function holdStaleChunkReload(): boolean {
  if (heldReloadOffers <= 0) return false;
  const first = !staleChunkHeld;
  staleChunkHeld = true;
  if (first) {
    // How often an update lands on somebody's unsaved work. Same no-op
    // without a DSN as the breadcrumb above.
    captureSentryMessage('stale-chunk reload held', 'info');
  }
  for (const listener of [...staleChunkHoldListeners]) {
    try {
      listener();
    } catch {
      // One broken listener must not stop the pill hearing it.
    }
  }
  return true;
}

/** Test-only reset. Never call this from app code. */
export function resetStaleChunkHoldForTests(): void {
  staleChunkHeld = false;
  heldReloadOffers = 0;
  staleChunkHoldListeners.clear();
}

export { RELOAD_COOLDOWN_MS };
